import {
  Box,
  Flex,
  Spacer,
  Button,
  IconButton,
  Text,
  Avatar,
  useColorMode,
  useColorModeValue,
} from "@chakra-ui/react";
import { useNavigate, useLocation } from "react-router-dom";
import { LogOut, Sun, Moon, Menu as MenuIcon } from "lucide-react";
import { useMemo, useState, type ElementType } from "react";

import { OnflyLogo } from "./OnflyLogo";
import { GGMenuItem, type NavSubItem } from "./MenuItem";
import { Sidebar } from "./Sidebar";
import { MENU_SECTIONS, isItemVisible } from "../config/menu";
import { useLogout } from "../hooks/useLogout";
import { getUser, hasRole } from "../lib/auth";
import { usePermissionsContext } from "../data/permissions/permissionContext";

const ONFLY_BLUE = "#009EFB";

export const TopNavbar = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { logout, loading: loggingOut } = useLogout();
  const { colorMode, toggleColorMode } = useColorMode();
  const { permissions } = usePermissionsContext();
  const user = useMemo(() => getUser(), [location.pathname]);
  const avatarSrc = user?.picture ?? undefined;
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);

  const navBg = useColorModeValue("white", "#0f1f3a");
  const navShadow = useColorModeValue(
    "0 1px 0 rgba(0,0,0,0.07), 0 4px 20px -8px rgba(0,0,0,0.10)",
    "0 1px 0 rgba(255,255,255,0.05), 0 4px 20px -8px rgba(0,0,0,0.6)"
  );
  const buttonHoverBg = useColorModeValue("gray.100", "whiteAlpha.100");
  const brandTextColor = useColorModeValue("#0a0a0a", "#fff");

  const hasAnyPermission = (required: string[]) =>
    required.some((p) => permissions.includes(p));

  const visibleSections = useMemo(() =>
    MENU_SECTIONS.filter((section) => {
      if (section.requiredRole && !hasRole(section.requiredRole)) return false;
      if (section.requiredRoles?.length && !section.requiredRoles.some((r) => hasRole(r))) return false;
      if (section.requiredPermissions?.length && !hasAnyPermission(section.requiredPermissions)) return false;
      return true;
    }),
    [permissions]
  );

  const transformSubitems = (items: typeof MENU_SECTIONS[0]["subitems"] = []): NavSubItem[] =>
    (items ?? [])
      .filter((item) => isItemVisible(item, permissions))
      .map((item) => ({
        name: item.name,
        icon: item.icon as ElementType | undefined,
        action: item.path ? () => navigate(item.path!) : undefined,
        path: item.path,
        subitems: item.subitems ? transformSubitems(item.subitems) : undefined,
      }));

  return (
    <>
      <Sidebar isOpen={isSidebarOpen} onClose={() => setIsSidebarOpen(false)} />
      <Box
        as="nav"
        bg={navBg}
        boxShadow={navShadow}
        py={3}
        px={{ base: 3, md: 5 }}
        pl={{ base: 3, md: 8 }}
        position="fixed"
        top={0}
        left={0}
        right={0}
        zIndex={1000}
      >
        <Flex align="center">
          {/* Hamburger — mobile only */}
          <IconButton
            display={{ base: "flex", md: "none" }}
            aria-label="Abrir menu"
            icon={<MenuIcon size={20} />}
            variant="ghost"
            mr={2}
            _hover={{ bg: buttonHoverBg }}
            onClick={() => setIsSidebarOpen(true)}
          />

          {/* Logo */}
          <Box
            pr={{ base: 3, md: 8 }}
            cursor="pointer"
            onClick={() => navigate("/")}
            aria-label="GG Hub"
          >
            <OnflyLogo dark={colorMode === "dark"} height={50} />
            <Text
              display={{ base: "none", md: "block" }}
              fontSize="xs"
              fontWeight="600"
              lineHeight="1"
              letterSpacing="-0.01em"
              color={brandTextColor}
            >
              GG{" "}
              <Text as="span" color={ONFLY_BLUE}>
                Hub
              </Text>
            </Text>
          </Box>

          {/* Menu items — desktop only */}
          <Flex display={{ base: "none", md: "flex" }} overflowX="auto" whiteSpace="nowrap" gap={4}>
            {visibleSections.map((section) => (
              <GGMenuItem
                key={section.name}
                name={section.name}
                icon={section.icon as ElementType}
                subitems={transformSubitems(section.subitems)}
                activePath={location.pathname}
              />
            ))}
          </Flex>

          <Spacer />

          {/* Avatar do usuário */}
          <Avatar
            size="sm"
            name={user?.name ?? ""}
            src={avatarSrc}
            bg={ONFLY_BLUE}
            color="white"
            fontWeight="600"
            mr={2}
          />

          {/* Tema */}
          <IconButton
            aria-label={colorMode === "dark" ? "Ativar tema claro" : "Ativar tema escuro"}
            icon={colorMode === "dark" ? <Sun size={18} /> : <Moon size={18} />}
            onClick={toggleColorMode}
            variant="ghost"
            mr={2}
            _hover={{ bg: buttonHoverBg }}
          />

          {/* Sair */}
          <Button
            leftIcon={<LogOut size={16} />}
            variant="ghost"
            fontWeight="semibold"
            isLoading={loggingOut}
            loadingText="Saindo..."
            _hover={{ bg: buttonHoverBg, color: "red.500" }}
            onClick={logout}
            px={{ base: 2, md: 4 }}
          >
            <Box display={{ base: "none", md: "inline" }}>Sair</Box>
          </Button>
        </Flex>
      </Box>
    </>
  );
};
